<template>
    <div class="kanban-group-selector">
        <a-popover
            v-model="visible"
            trigger="click"
            placement="bottomLeft"
            overlay-class-name="group-selector-popover"
        >
            <template #content>
                <div class="selector-content">
                    <div class="selector-header">
                        <div class="selector-title">Gruplama Alanı Seç</div>
                        <a-input
                            v-model="searchQuery"
                            placeholder="Ara..."
                            size="small"
                            class="search-input"
                        >
                            <i slot="prefix" class="material-icons">search</i>
                        </a-input>
                    </div>

                    <div class="selector-body">
                        <div 
                            v-for="category in filteredCategories" 
                            :key="category.key"
                            class="category-section"
                        >
                            <div class="category-title">
                                <i class="material-icons category-icon">{{ category.icon }}</i>
                                {{ category.label }}
                            </div>
                            <div class="category-items">
                                <div
                                    v-for="field in category.fields"
                                    :key="field.key"
                                    class="field-item"
                                    :class="{ 'is-active': selectedField === field.key }"
                                    @click="handleFieldSelect(field)"
                                >
                                    <i class="material-icons field-icon">{{ field.icon }}</i>
                                    <span class="field-label">{{ field.label }}</span>
                                    <i v-if="selectedField === field.key" class="material-icons check-icon">check</i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <div class="selector-trigger" :class="{ 'is-open': visible }">
                <i class="material-icons trigger-icon">{{ currentFieldIcon }}</i>
                <span class="trigger-label">{{ currentFieldLabel }}</span>
                <i class="material-icons arrow-icon">{{ visible ? 'expand_less' : 'expand_more' }}</i>
            </div>
        </a-popover>
    </div>
</template>

<script>
import { Popover, Input } from 'ant-design-vue';

const GROUP_FIELD_CATEGORIES = [
    {
        key: 'basic',
        label: 'Temel',
        icon: 'dashboard',
        fields: [
            { key: 'answerStatus', label: 'Cevap Durumu', icon: 'question_answer' },
            { key: 'lastConversationStatus', label: 'Sohbet Durumu', icon: 'chat' },
            { key: 'owner', label: 'Sahip', icon: 'person' },
            { key: 'users', label: 'Katılımcılar', icon: 'group' },
            { key: 'channel', label: 'Kanal', icon: 'alternate_email' },
            { key: 'platform', label: 'Platform', icon: 'devices' },
        ],
    },
    {
        key: 'demographic',
        label: 'Demografik',
        icon: 'public',
        fields: [
            { key: 'country', label: 'Ülke', icon: 'flag' },
            { key: 'city', label: 'Şehir', icon: 'location_city' },
            { key: 'browser', label: 'Tarayıcı', icon: 'web' },
            { key: 'preferredLang', label: 'Dil', icon: 'language' },
        ],
    },
    {
        key: 'tags',
        label: 'Etiket & Kategori',
        icon: 'label',
        fields: [
            { key: 'tags', label: 'Etiketler', icon: 'label' },
            { key: 'department', label: 'Departman', icon: 'apartment' },
        ],
    },
    {
        key: 'meta',
        label: 'Meta Leads',
        icon: 'campaign',
        fields: [
            { key: 'metaLeadStatus', label: 'Lead Durumu', icon: 'trending_up' },
            { key: 'metaLeadAdName', label: 'Reklam Adı', icon: 'ad_units' },
            { key: 'metaLeadAdsetName', label: 'Reklam Seti', icon: 'view_module' },
            { key: 'metaLeadCampaignName', label: 'Kampanya', icon: 'campaign' },
            { key: 'metaLeadFormName', label: 'Form Adı', icon: 'article' },
            { key: 'metaLeadPlatform', label: 'Meta Platform', icon: 'facebook' },
        ],
    },
    {
        key: 'broadcast',
        label: 'Broadcast',
        icon: 'send',
        fields: [
            { key: 'lastBroadcastStatus', label: 'Broadcast Durumu', icon: 'send' },
            { key: 'lastBroadcastReason', label: 'Broadcast Nedeni', icon: 'info' },
        ],
    },
    {
        key: 'other',
        label: 'Diğer',
        icon: 'more_horiz',
        fields: [
            { key: 'blockedSnapshot', label: 'Engel Durumu', icon: 'block' },
            { key: 'lastTriggerType', label: 'Tetikleme Tipi', icon: 'flash_on' },
            { key: 'zohoLeadStatus', label: 'Zoho Lead Durumu', icon: 'business' },
            { key: 'zohoLeadSource', label: 'Zoho Lead Kaynağı', icon: 'source' },
        ],
    },
];

export default {
    name: 'KanbanGroupSelector',
    components: {
        'a-popover': Popover,
        'a-input': Input,
    },
    props: {
        value: {
            type: String,
            default: 'answerStatus',
        },
    },
    data() {
        return {
            visible: false,
            searchQuery: '',
            categories: GROUP_FIELD_CATEGORIES,
        };
    },
    computed: {
        selectedField() {
            return this.value;
        },
        currentFieldInfo() {
            for (const category of this.categories) {
                const field = category.fields.find(f => f.key === this.selectedField);
                if (field) return field;
            }
            return { key: this.selectedField, label: this.selectedField, icon: 'filter_list' };
        },
        currentFieldLabel() {
            return this.currentFieldInfo.label;
        },
        currentFieldIcon() {
            return this.currentFieldInfo.icon;
        },
        filteredCategories() {
            if (!this.searchQuery.trim()) {
                return this.categories;
            }
            
            const query = this.searchQuery.toLowerCase();
            return this.categories
                .map(category => {
                    return {
                        ...category,
                        fields: category.fields.filter(field => 
                            field.label.toLowerCase().includes(query) ||
                            field.key.toLowerCase().includes(query)
                        ),
                    };
                })
                .filter(category => category.fields.length > 0);
        },
    },
    methods: {
        handleFieldSelect(field) {
            this.$emit('input', field.key);
            this.$emit('change', field.key);
            this.visible = false;
            this.searchQuery = '';
        },
    },
};
</script>

<style lang="scss" scoped>
.kanban-group-selector {
    display: inline-block;
}

.selector-trigger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 16px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
    
    &:hover {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
    
    &.is-open {
        border-color: #3b82f6;
        box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    }
}

.trigger-icon {
    font-size: 20px;
    color: #6b7280;
}

.trigger-label {
    font-size: 14px;
    font-weight: 500;
    color: #1f2937;
}

.arrow-icon {
    font-size: 20px;
    color: #9ca3af;
    margin-left: 4px;
}
</style>

<style lang="scss">
.group-selector-popover {
    .ant-popover-inner {
        padding: 0;
        border-radius: 12px;
        box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
    }
    
    .ant-popover-inner-content {
        padding: 0;
    }
}

.selector-content {
    width: 360px;
    max-height: 520px;
    display: flex;
    flex-direction: column;
}

.selector-header {
    padding: 16px;
    border-bottom: 1px solid #e5e7eb;
    background: #f9fafb;
}

.selector-title {
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 12px;
}

.search-input {
    .ant-input {
        border-radius: 6px;
        
        &:focus {
            border-color: #3b82f6;
            box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }
    }
    
    .material-icons {
        font-size: 18px;
        color: #9ca3af;
    }
}

.selector-body {
    overflow-y: auto;
    max-height: 420px;
    
    &::-webkit-scrollbar {
        width: 6px;
    }
    
    &::-webkit-scrollbar-track {
        background: #f9fafb;
    }
    
    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 3px;
        
        &:hover {
            background: #9ca3af;
        }
    }
}

.category-section {
    border-bottom: 1px solid #e5e7eb;
    
    &:last-child {
        border-bottom: none;
    }
}

.category-title {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: #f9fafb;
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.category-icon {
    font-size: 16px;
}

.category-items {
    padding: 4px;
}

.field-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    margin: 2px 0;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.15s ease;
    
    &:hover {
        background: #f3f4f6;
    }
    
    &.is-active {
        background: #eff6ff;
        color: #3b82f6;
        
        .field-icon {
            color: #3b82f6;
        }
    }
}

.field-icon {
    font-size: 20px;
    color: #9ca3af;
    flex-shrink: 0;
}

.field-label {
    flex: 1;
    font-size: 14px;
    color: #374151;
    
    .is-active & {
        color: #3b82f6;
        font-weight: 500;
    }
}

.check-icon {
    font-size: 20px;
    color: #3b82f6;
    flex-shrink: 0;
}
</style>

